import React from 'react'
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  FormControl,
  InputLabel,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { useForm, Controller } from 'react-hook-form'
import { EMAIL_REGEX, PASSWORD_REGEX } from 'helpers/constants'
import { useTranslation } from 'react-i18next'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Visibility from '@mui/icons-material/Visibility'
import { useSnackbar } from 'notistack'
import UserService from 'api/services/User'
import { queryClient } from 'api'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import usePageView from 'hooks/usePageView'

interface FieldsForCreateNewUser {
  username: string
  email: string
  password: string
  firstName: string
  lastName: string
  mobileNumber: string
  role: string
}

const DefaultValues = {
  username: '',
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  mobileNumber: '',
  role: '',
}

function CreateNewUser() {
  usePageView()
  const [showPassword, setShowPassword] = React.useState<boolean>(false)

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
  }

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev)
  }

  const { control, handleSubmit, reset } = useForm<FieldsForCreateNewUser>({
    defaultValues: DefaultValues,
  })

  const { executeRecaptcha } = useGoogleReCaptcha()

  const createGoogleToken = React.useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }
    return await executeRecaptcha()
  }, [executeRecaptcha])

  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const createUserMutation = UserService.hooks.useCreateUser({
    onSuccess: () => {
      enqueueSnackbar(t('signUpPage.successMessage'), {
        variant: 'success',
      })

      queryClient.invalidateQueries(UserService.keys.QueryKeys.all)
      queryClient.invalidateQueries(UserService.keys.QueryKeys.count)

      reset(DefaultValues)
    },
    onError: (error) => {
      enqueueSnackbar(t('signUpPage.failedMessage'), {
        variant: 'error',
      })
    },
  })
  const onSubmit = async (data) => {
    const token = await createGoogleToken()
    createUserMutation.mutate({ ...data, token })
  }

  const roles = [
    {
      label: 'User',
      value: 'USER',
    },
    {
      label: 'Admin',
      value: 'ADMIN',
    },
  ]

  return (
    <>
      <Box
        sx={{
          justifyContent: 'center',
          display: 'flex',
          pb: 5,
        }}
      >
        <Box
          sx={{
            maxWidth: (theme) => theme.breakpoints.values.lg,
            border: '1px solid',
            borderColor: grey[300],
            boxShadow: 25,
            borderRadius: '8px',
            mt: { xs: '56px', md: '10px' },
            p: '24px',
            bgcolor: 'background.paper',
          }}
        >
          <Grid
            container
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            spacing={2}
          >
            <Grid item xs={12}>
              <Typography variant="h6" textAlign="center">
                {t('dashboard.pages.createUser.title')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                rules={{
                  required: t('validation.fieldRequired') as unknown as string,
                  pattern: {
                    value: EMAIL_REGEX,
                    message: t(
                      'validation.emailPatternError'
                    ) as unknown as string,
                  },
                }}
                name="username"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label={`${t('userSignUpPage.email')}*`}
                    variant="filled"
                    error={!!error}
                    helperText={error ? error?.message : ' '}
                    fullWidth
                    sx={{
                      color: 'common.black',
                      borderColor: grey[300],
                    }}
                    {...field}
                  ></TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="password"
                control={control}
                rules={{
                  required: t('validation.fieldRequired') as unknown as string,
                  pattern: {
                    value: PASSWORD_REGEX,
                    message: t('error.password'),
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label={t('userSignUpPage.password')}
                    variant="filled"
                    error={!!error}
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    helperText={error ? error?.message : ' '}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            color={showPassword ? 'primary' : 'default'}
                            onClick={handleTogglePassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      color: 'common.black',
                      borderColor: grey[300],
                    }}
                    {...field}
                  ></TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                rules={{
                  required: false,
                }}
                name="firstName"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label={t('userSignUpPage.firstName')}
                    variant="filled"
                    error={!!error}
                    helperText={error ? error?.message : ' '}
                    fullWidth
                    sx={{
                      color: 'common.black',
                      borderColor: grey[300],
                    }}
                    {...field}
                  ></TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                rules={{
                  required: false,
                }}
                name="lastName"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label={t('userSignUpPage.lastName')}
                    variant="filled"
                    error={!!error}
                    helperText={error ? error?.message : ' '}
                    fullWidth
                    sx={{
                      color: 'common.black',
                      borderColor: grey[300],
                    }}
                    {...field}
                  ></TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                rules={{
                  required: false,
                }}
                name="mobileNumber"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label={`${t('userSignUpPage.phoneNumber')}`}
                    variant="filled"
                    error={!!error}
                    helperText={error ? error?.message : ' '}
                    fullWidth
                    sx={{
                      color: 'common.black',
                      borderColor: grey[300],
                    }}
                    {...field}
                  ></TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                rules={{
                  required: false,
                  pattern: {
                    value: EMAIL_REGEX,
                    message: t(
                      'validation.emailPatternError'
                    ) as unknown as string,
                  },
                }}
                name="email"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label={`${t('userSignUpPage.contactEmail')}`}
                    variant="filled"
                    error={!!error}
                    helperText={error ? error?.message : ' '}
                    fullWidth
                    sx={{
                      color: 'common.black',
                      borderColor: grey[300],
                    }}
                    {...field}
                  ></TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                rules={{ required: true }}
                name="role"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl variant="filled" fullWidth>
                    <InputLabel>{`${t('userSignUpPage.role')}*`}</InputLabel>
                    <Select
                      fullWidth
                      labelId="role"
                      id="role"
                      variant="filled"
                      error={!!error}
                      {...field}
                    >
                      <MenuItem value="">
                        {t('dashboard.pages.createUser.selectRole')}
                      </MenuItem>
                      {roles.map((rol) => {
                        return (
                          <MenuItem key={rol.value} value={rol.value}>
                            {rol.label}
                          </MenuItem>
                        )
                      })}
                    </Select>

                    <FormHelperText error={Boolean(error)}>
                      {error ? error?.message : ' '}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                sx={{ width: '100%', textTransform: 'none' }}
              >
                {createUserMutation.isLoading ? (
                  <CircularProgress
                    sx={{
                      '& svg': {
                        color: 'background.paper',
                      },
                    }}
                    size={30}
                  />
                ) : (
                  <Typography fontSize={20}>
                    {t('dashboard.pages.createUser.createButton')}
                  </Typography>
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  )
}

export default CreateNewUser
